import * as classNames from "classnames";
import { EDA } from "motw_core/Dreams/EDreamAttribute";
import IDream from "motw_core/Entity/IDream";
import * as React from "react";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { DreamControls } from "source/app/presentation/MainPanel/Dreams/Controls/DreamControls";
import { DreamDifficultyQuickSelector } from "source/app/presentation/MainPanel/Dreams/Controls/Difficulty/QuickSelector/DreamDifficultyQuickSelector";
import { DreamName } from "source/app/presentation/MainPanel/Dreams/DreamName/DreamName";
import { useDreamNameExpansion } from "source/app/presentation/MainPanel/Dreams/NameExpansion/DreamNameExpansionContext";
import { EDragDropObject } from "source/app/services/Shared/EDragDropObject";
import { useDragSource } from "source/app/services/Shared/Hooks/useDragSource";
import DreamAttributeIcons from "../DreamAttributeIcon/DreamAttributeIcon";
import { DreamNoteIcon } from "../DreamAttributeIcon/DreamNoteIcon/DreamNoteIcon";
import DreamHelpers, { DreamEmotionSymbol } from "../DreamHelpers/DreamHelpers";
import { IsMasterDream } from "../DreamHelpers/IsMasterDream";
import DreamPanelLink from "../DreamPanelLink/DreamPanelLink";
import { GetDreamRowFontSize } from "../Helpers/GetDreamRowFontSize";
import { GetDreamRowOpacity } from "../Helpers/GetDreamRowOpacity";
import { DreamRowDropTargets } from "./DreamRowDropTargets";
import { GetDreamRowLayoutStyle } from "./GetDreamRowLayoutStyle";
import { DreamNameExpansionButton } from "./NameExpansion/DreamNameExpansionButton";
import * as localStyles from "./DreamRow.css";
import { IDreamRowOptions } from "./IDreamRowOptions";
import { hashTag_p_boredom } from "motw_core/Constants/constants";
import { AreEqual } from "source/app/services/Shared/ReactHelpers";

export const randomDreamTarget = "Dreams_randomDream";

function DreamRowComponent(row: IDreamRowOptions) {
    const { dream, sortedData, searchText } = row;
    const isMobileDevice = IsMobileDevice();
    const [showDifficultySelector, setShowDifficultySelector] =
        React.useState(false);
    const [isRecentlyDifficultyChanged, setIsRecentlyDifficultyChanged] =
        React.useState(false);
    const difficultyChangedTimeout = React.useRef<number>();
    const {
        expandableDreamIds,
        expandedDreamIds,
        setDreamExpandable,
        toggleDream,
        collapseDream,
    } = useDreamNameExpansion();

    const dragSourceResult = useDragSource(EDragDropObject.dreamRow, row.dream, [
        row.dream.id,
    ]);

    const isMasterDream = IsMasterDream(dream, sortedData);
    const isNotLinked = !DreamHasLinks(dream, sortedData);

    const isProblem = dream.attributes.includes(EDA[EDA.problem]);
    const isSuccessDream = dream.name.trimStart().startsWith(`+`);

    const isHighDifficulty = row.selectedDifficulty !== 0 &&
        dream.difficulty > row.selectedDifficulty;
    const shouldShowNameExpansion = expandableDreamIds.has(dream.id);
    const isNameExpanded = expandedDreamIds.has(dream.id);
    const shouldHideDifficultyArrows =
        shouldShowNameExpansion && !isNameExpanded;
    const handleNameOverflowChange = React.useCallback(
        (isOverflowing: boolean) => {
            setDreamExpandable(dream.id, isMobileDevice && isOverflowing);
        },
        [dream.id, isMobileDevice, setDreamExpandable]
    );

    React.useEffect(() => {
        collapseDream(dream.id);
        return () => setDreamExpandable(dream.id, false);
    }, [collapseDream, dream.id, dream.name, setDreamExpandable]);

    React.useEffect(
        () => () => window.clearTimeout(difficultyChangedTimeout.current),
        []
    );

    const indicateDifficultyChanged = React.useCallback(() => {
        setIsRecentlyDifficultyChanged(true);
        window.clearTimeout(difficultyChangedTimeout.current);
        difficultyChangedTimeout.current = window.setTimeout(
            () => setIsRecentlyDifficultyChanged(false),
            900
        );
    }, []);

    return (
        <div
            id={row.isRandomDream ? randomDreamTarget : `dreamRow_${dream.id}`}
            data-dream-row
            data-difficulty={dream.difficulty}
            key={dream.id}
            style={{
                fontSize: GetDreamRowFontSize(dream.difficulty),
                ...(isSuccessDream ? successDreamRowStyle : {}),
                ...(isRecentlyDifficultyChanged
                    ? recentlyChangedDreamRowStyle
                    : {}),
            }}
            className={classNames([row.withClass], [localStyles.row], {
                [localStyles.problem]: isProblem,
                [localStyles.randomDream]: row.isRandomDream,
                [localStyles.highDifficulty]: isHighDifficulty,
                [localStyles.rowExpanded]: isNameExpanded,
            })}
        >
            <span
                style={GetDreamRowLayoutStyle({
                    shouldShowNameExpansion,
                    isNameExpanded,
                    opacity: GetDreamRowOpacity(
                        dream.difficulty,
                        dragSourceResult.isDragging
                    ),
                })}
            >
                <span style={{ flex: `0 0 auto` }}>
                    <DreamPanelLink dream={dream} isNotLinked={isNotLinked}>
                        <DreamAttributeIcons dream={dream} showAllIcons />
                        <DreamEmotionSymbol
                            symbol={dream.emotion}
                            doesHaveLinks={!isNotLinked}
                        />
                        <DreamNoteIcon dream={dream} />
                    </DreamPanelLink>
                </span>
                <span
                    ref={dragSourceResult.dragSourceRef}
                    onClickCapture={(event) => {
                        if (!dragSourceResult.consumePostDragClick()) {
                            return;
                        }

                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    style={{
                        display: `flex`,
                        alignItems: isNameExpanded ? `flex-start` : `center`,
                        flexDirection: isNameExpanded ? `column` : `row`,
                        flexWrap: `nowrap`,
                        columnGap: `4px`,
                        rowGap: isNameExpanded ? `4px` : `2px`,
                        flex: `1 1 0`,
                        width: `100%`,
                        minWidth: 0,
                        maxWidth: `100%`,
                        whiteSpace: `nowrap`,
                    }}
                >
                    <span
                        style={{
                            display: `block`,
                            flex: `1 1 auto`,
                            minWidth: 0,
                            maxWidth: `100%`,
                            overflow: isNameExpanded ? `visible` : `hidden`,
                            textOverflow: isNameExpanded
                                ? `clip`
                                : `ellipsis`,
                            whiteSpace: isNameExpanded ? `normal` : `nowrap`,
                        }}
                    >
                        <DreamName
                            {...{
                                dream,
                                sortedData,
                                isMasterDreamWithChildren:
                                    isMasterDream && !isNotLinked,
                                showTooltipOnlyWhenTruncated: true,
                                isExpanded: isNameExpanded,
                                collapsedLineCount: isMobileDevice ? 2 : 1,
                                onOverflowChange: handleNameOverflowChange,
                            }}
                        />
                    </span>
                    <span
                        className={
                            isNameExpanded
                                ? localStyles.expandedDreamControls
                                : undefined
                        }
                        style={{
                            flex: `0 0 auto`,
                            marginLeft: isNameExpanded ? `0` : `auto`,
                        }}
                    >
                        <DreamControls
                            dream={dream}
                            showSelector={showDifficultySelector}
                            setShowSelector={setShowDifficultySelector}
                            onDifficultyChanged={indicateDifficultyChanged}
                            hideDifficultyArrows={shouldHideDifficultyArrows}
                        />
                    </span>
                </span>
                {shouldShowNameExpansion && isNameExpanded && (
                    <span className={localStyles.expandedDropTargetsSlot}>
                        <DreamRowDropTargets dream={dream} />
                    </span>
                )}
                {shouldShowNameExpansion && (
                    <DreamNameExpansionButton
                        isExpanded={isNameExpanded}
                        onToggle={() => toggleDream(dream.id)}
                    />
                )}
            </span>
            <DreamDifficultyQuickSelector
                show={showDifficultySelector}
                setShow={setShowDifficultySelector}
                dream={dream}
            />
            {(!shouldShowNameExpansion || !isNameExpanded) && (
                <DreamRowDropTargets dream={dream} />
            )}
        </div>
    );
}

export const DreamRow = React.memo(DreamRowComponent, AreEqual);

const recentlyChangedDreamRowStyle: React.CSSProperties = {
    background: `hsl(42deg 72% 52% / 0.12)`,
    boxShadow: `inset 3px 0 hsl(47deg 88% 78%), inset -3px 0 hsl(47deg 88% 78%), 0 0 9px hsl(43deg 85% 62% / 0.28)`,
};

const successDreamRowStyle: React.CSSProperties = {
    backgroundImage: `
        linear-gradient(
            90deg,
            hsl(220deg 10% 12% / 0.96) 0%,
            hsl(220deg 10% 12% / 0) 22%,
            hsl(220deg 10% 12% / 0) 78%,
            hsl(220deg 10% 12% / 0.96) 100%
        ),
        repeating-linear-gradient(
            135deg,
            hsl(86deg 72% 58% / 0.025) 0,
            hsl(86deg 72% 58% / 0.09) 2px,
            hsl(86deg 72% 58% / 0.09) 3px,
            hsl(86deg 72% 58% / 0.04) 6px,
            hsl(98deg 34% 18% / 0) 8px,
            hsl(98deg 34% 18% / 0) 10px
        ),
        linear-gradient(
            90deg,
            hsl(88deg 28% 16% / 0.4),
            hsl(94deg 20% 11% / 0.24)
        )
    `,
    boxShadow: `
        inset 0 1px hsl(86deg 68% 70% / 0.1),
        inset 0 -1px hsl(98deg 38% 4% / 0.22),
        0 0 8px hsl(86deg 68% 52% / 0.12)
    `,
};

export const DreamHasLinks = (row: IDream, sortedData: IDream[]) =>
    DreamHelpers.GetAllLinks(row, sortedData).length > 1;
